<template>
	<EditorShell>
		<!-- Header -->
		<CLayoutEditorHeader
			:isLoading="isLoading"
			:headline="template ? template.name : null"
			:saveButton="$t('templates.save')"
			@back:click="$router.back()"
			@save:click="updateTemplate"
		>
			<EditorSaveChangesPrompt :isVisible="hasChanged" slot="middle" />
		</CLayoutEditorHeader>

		<!-- Wrapper -->
		<JBox class="s-px-4 s-max-w-4xl s-mx-auto">
			<!-- Preview switcher -->
			<EditorPreviewSwitcher
				class="s-mt-4 s-h-10"
				:showPreview="showPreview"
				:label="$t('templates.graphQlVariables')"
				v-model="previewVariables"
				@preview:toggle="showPreview = $event"
				@preview:open="renderPreview"
				@input="invalidateMemoizedDataForPreview"
				@render:click="renderPreview"
			/>

			<!-- Editor and rendered preview -->
			<JBox>
				<JFlex class="s-bg-white s-rounded-md s-border s-border-gray-300 s-p-5 s-mt-1 s-mb-4">
					<!-- Editor -->
					<JBox class="s-w-full">
						<JHeadline class="s-text-gray-700 s-font-semibold s-mb-2 s-text-xl">{{ $t("templates.smsNotification") }}</JHeadline>
						<JInputLongText v-model="template.bodyLiquid" />
						<JButton variant="white-sm" @click="showLiquidPicker = !showLiquidPicker">{{ $t("templates.liquidPicker") }}</JButton>
					</JBox>

					<!-- Preview -->
					<Transition enter-active-class="animated fadeIn faster">
						<JBox v-if="showPreview" class="s-w-full s-pl-4">
							<JHeadline class="s-font-semibold s-text-gray-700 s-mb-2 s-text-xl">{{ $t("templates.renderedSms") }}</JHeadline>
							<JBox class="s-border s-border-gray-300 s-shadow s-p-2 s-rounded" v-if="!isPreviewLoading">
								{{ previewResult }}
							</JBox>
							<JSpinner variant="indigo" type="dots" :delay="100" v-else />
						</JBox>
					</Transition>
				</JFlex>
			</JBox>

			<!-- Document Settings -->
			<EditorAccordion :headline="$t('templates.document_settings')" class="s-mb-4">
				<EditorDocumentSettings v-model="template" />
			</EditorAccordion>

			<!-- Template Graphql Queries -->
			<EditorAccordion :headline="$t('core.graphQlQuery')" class="s-mb-4">
				<CGraphQlQueryLibrary v-model="template.graphQlQuery" @input="invalidateMemoizedDataForPreview" />
			</EditorAccordion>

			<!-- Delete template -->
			<CDialogProvider
				:headline="$t('templates.delete_template')"
				:description="$t('templates.delete_dialog')"
				:cancelButton="$t('templates.continueEditing')"
				:confirmButton="$t('templates.delete_template')"
				@confirm="deleteTemplate"
			>
				<JFlex class="s-justify-end" slot-scope="{ openDialog }">
					<JButton variant="error" @click="openDialog">{{ $t("templates.delete_template") }} </JButton>
				</JFlex>
			</CDialogProvider>
		</JBox>

		<!-- Liquid Picker -->
		<CModal :isVisible="showLiquidPicker" @overlay:click="showLiquidPicker = false">
			<JBox class="s-p-4">
				<CLiquidPicker @data:selected="showLiquidPicker = false" />
			</JBox>
		</CModal>

		<!-- Debug Console -->
		<EditorDebugConsole :errors="errors" />
	</EditorShell>
</template>

<script>
import EditorShell from "../components/EditorShell"
import EditorAccordion from "../components/EditorAccordion"
import EditorSaveChangesPrompt from "../components/EditorSaveChangesPrompt"
import EditorPreviewSwitcher from "../components/EditorPreviewSwitcher"
import EditorDocumentSettings from "../components/EditorDocumentSettings"
import EditorDebugConsole from "../components/EditorDebugConsole"
import { preventBrowserBackButton } from "../utils/preventBrowserBackButton"
import api from "@/api/api"
import { query, mutation } from "../api/queries"
import isEqual from "lodash.isequal"
import { deepClone } from "@/utils/"
import { addHintToLiquidErrorString } from "../utils/addHintToLiquidErrorString"

export default {
	components: {
		EditorShell,
		EditorAccordion,
		EditorSaveChangesPrompt,
		EditorPreviewSwitcher,
		EditorDocumentSettings,
		EditorDebugConsole
	},

	props: {
		id: {
			type: String
		}
	},

	data() {
		return {
			isLoading: false,
			template: {}, // state for forms
			originalTemplate: {}, // original to compare changes against

			// Liquid Picker
			showLiquidPicker: false,

			// Preview
			showPreview: false,
			isPreviewLoading: false,
			previewResult: null,
			previewVariables: null, // user can define what variables to pass
			previewData: null, // cached data fetched from the API to be passed to renderPreview

			// Errors for debug console
			errors: []
		}
	},

	computed: {
		hasChanged() {
			return !isEqual(this.template, this.originalTemplate)
		}
	},

	methods: {
		async fetchTemplate() {
			if (this.isLoading) return
			this.setErrors() // reset

			this.isLoading = true
			const res = await api({
				query: query.fetchSmsTemplate,
				variables: {
					where: { id: this.id }
				}
			})
			this.isLoading = false

			// Handle errors
			if (!res?.data?.templates?.list?.[0]) {
				this.setErrors(res?.errors?.map(item => item?.message) || [this.$t("templates.noTemplate")])
				return
			}

			this.template = res?.data?.templates?.list?.[0] ?? {}
			this.originalTemplate = deepClone(res?.data?.templates?.list?.[0] ?? {}) // important to create a copy so that isEqual is working properly in hasChanged
		},

		async updateTemplate() {
			if (this.isLoading) return
			this.setErrors() // reset

			this.isLoading = true
			const res = await api({
				query: mutation.updateSmsTemplate,
				variables: {
					templateId: this.template?.id,
					input: {
						name: this.template?.name,
						description: this.template?.description,
						bodyLiquid: this.template?.bodyLiquid,
						graphQlQuery: this.template?.graphQlQuery
					}
				}
			})
			this.isLoading = false

			// Errors
			if (res?.data?.updateSmsTemplate?.status !== "success") {
				this.setErrors(res?.data?.updateSmsTemplate?.errors || res?.errors?.map(item => item?.message))
				return
			}

			this.originalTemplate = deepClone(this.template) // update also original so that comparison works
		},

		async deleteTemplate() {
			if (this.isLoading) return
			this.setErrors() // reset

			this.isLoading = true
			const res = await api({
				query: mutation.deleteTemplate,
				variables: { templateId: this.id }
			})
			this.isLoading = false

			// Errors
			if (res.data?.deleteTemplate?.status !== "success") {
				this.setErrors(res?.data?.deleteTemplate?.errors || res?.errors?.map(item => item?.message))
				return
			}

			this.$router.push({ name: "ViewTemplatesHome" })
		},

		async getMemoizedDataForPreview() {
			// 1: Get it from cache
			if (this.previewData) {
				return Promise.resolve({ isSuccess: true, errors: null, data: this.previewData })
			}

			// 2: If not in cache, fetch it
			const query = this?.template?.graphQlQuery ?? ""

			let variables = { limit: 1 } // putting hard limit to not overfetch if no variables provided, if limit is not defined in query though can cause problems anyways

			// If no query, it means there is no query needed
			if (!query) return Promise.resolve({ isSuccess: true, errors: null, data: null })

			// Parse variables safely
			try {
				variables = { ...variables, ...JSON.parse(this.previewVariables) }
			} catch (e) {
				return Promise.resolve({ isSuccess: false, errors: [e], data: null })
			}

			const res = await api({ query, variables })

			// Check errors of the query
			if (res?.errors) {
				return Promise.resolve({ isSuccess: false, errors: res?.errors?.map(item => item.message), data: null })
			}

			// update cache
			this.previewData = res?.data
			// Success
			return Promise.resolve({ isSuccess: true, data: res?.data, errors: null })
		},

		invalidateMemoizedDataForPreview() {
			this.previewData = null
		},

		async renderPreview() {
			if (this.isPreviewLoading) return
			this.isPreviewLoading = true
			this.previewResult = ""
			this.setErrors()

			// Get data for preview
			const { isSuccess, errors, data } = await this.getMemoizedDataForPreview()

			if (!isSuccess) {
				this.setErrors(errors, { hint: this.$t("templates.problemWithGql") })
				this.isPreviewLoading = false
				return
			}

			const res = await api({
				query: query.renderSms,
				variables: {
					bodyLiquid: this.template?.bodyLiquid,
					input: {
						name: "data",
						contentJsonString: data ? JSON.stringify(data) : "{}"
					}
				}
			})
			this.isPreviewLoading = false

			// Errors
			if (res?.data?.renderSms?.status !== "success") {
				this.setLiquidErrors(res?.data?.renderSms?.errors || res?.errors?.map(item => item?.message))
				return
			}

			this.previewResult = res?.data?.renderSms?.value?.body
		},

		setLiquidErrors(errors = []) {
			const output = errors.map(
				item => addHintToLiquidErrorString({ message: item, templateSource: this?.template?.bodyLiquid }) // returns {message, hint} object
			)
			this.errors = output
		},

		setErrors(errors = [], { hint = "" } = {}) {
			this.errors = errors?.map(err => ({ message: err, hint }))
		}
	},

	created() {
		this.fetchTemplate()
	},

	beforeRouteLeave(to, from, next) {
		preventBrowserBackButton({ next, showDialog: this.hasChanged })
	}
}
</script>
